import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { StateUtils } from '@spartacus/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { ItemListType } from '../../../features';
import { ActiveCartFacade } from '../../../features/cart/base';
import { UserIdService } from '../../auth';
import { GlobalMessageService, GlobalMessageType } from '../../global-message';
import { Article, CartType, ConsignmentArticleStock, LoaderError, NewFreeStock } from '../../model';
import { AssortmentConnector } from '../connectors/assortment.connector';
import { AssortmentFacade } from '../facades';
import { UserActions } from '../store/actions';
import { UserSelectors } from '../store/selectors';
import {
  getConsignmentStockArticleStockInfoLoaderState,
  getConsignmentStockArticleStockInfoStateError,
  getConsignmentStockArticleStockInfoStateLoading,
  getConsignmentStockArticleStockInfoStateSuccess,
} from '../store/selectors/assortment.selectors';
import { StateWithUser } from '../store/user-state';

export interface QuantityAndUnitValue {
  quantity: number;
  unitCode: string;
}
@Injectable()
export class AssortmentService implements AssortmentFacade {
  constructor(
    private store: Store<StateWithUser>,
    private userIdService: UserIdService,
    private assortmentConnector: AssortmentConnector,
    private globalMessageService: GlobalMessageService,
    private activeCartService: ActiveCartFacade
  ) {}

  requestNewFreeArticles(requestedNewFreeArticles: NewFreeStock[]): Observable<Blob> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.assortmentConnector.requestNewFreeArticles(userId, requestedNewFreeArticles)),
      catchError(() => {
        this.globalMessageService.add(
          { key: 'myAssortment.freeStock.requestNewFreeArticlesFailure_message' },
          GlobalMessageType.MSG_TYPE_ERROR
        );
        return EMPTY;
      })
    );
  }

  getConsignmentStockArticlesLoading(): Observable<boolean> {
    return this.store.select(UserSelectors.AssortmentSelectors.getConsignmentStockArticlesLoading);
  }

  getConsignmentStockArticlesLoaded(): Observable<boolean> {
    return this.store.select(UserSelectors.AssortmentSelectors.getConsignmentStockArticlesSuccess);
  }

  getConsignmentStockArticlesError(): Observable<LoaderError | undefined> {
    return this.store.select(UserSelectors.AssortmentSelectors.getConsignmentStockArticlesError);
  }

  getConsignmentStockArticlesRefs(): Observable<string[]> {
    return this.store.pipe(select(UserSelectors.AssortmentSelectors.getConsignmentStockArticlesCodes));
  }

  loadConsignmentStockArticles(): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadConsignmentStockArticles(userId)),
      () => {}
    );
  }

  resetConsignmentStockArticles(): void {
    this.store.dispatch(new UserActions.ResetConsignmentStockArticles());
  }

  resetConsignmentStockArticlesStockInfo(): void {
    this.store.dispatch(new UserActions.ResetConsignmentStockArticlesStockInfo());
  }

  getConsignmentStockArticleStockInfo(articleNumber: string): Observable<ConsignmentArticleStock> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap(() => this.store.select(getConsignmentStockArticleStockInfoLoaderState, { key: articleNumber })),
      tap((loaderState) => {
        if (this.needsToLoad(loaderState)) {
          this.loadConsignmentStockArticleStockInfo(articleNumber);
        }
      }),
      filter((loaderState) => loaderState.success && !!loaderState.value),
      map((loaderState) => loaderState.value)
    );
  }

  loadConsignmentStockArticleStockInfo(articleNumber: string): void {
    this.userIdService
      .takeUserId(true)
      .pipe(
        tap((userId) =>
          this.store.dispatch(new UserActions.LoadConsignmentStockArticleStockInfo({ userId, articleRef: articleNumber }))
        )
      )
      .subscribe();
  }

  getConsignmentStockArticleStockInfoLoading(articleRef: string): Observable<boolean> {
    return this.store.select(getConsignmentStockArticleStockInfoStateLoading, { key: articleRef });
  }

  getConsignmentStockArticleStockInfoSuccess(articleRef: string): Observable<boolean> {
    return this.store.select(getConsignmentStockArticleStockInfoStateSuccess, { key: articleRef });
  }

  getConsignmentStockArticleStockInfoError(articleRef: string): Observable<LoaderError> {
    return this.store.select(getConsignmentStockArticleStockInfoStateError, { key: articleRef });
  }

  addAssortmentEntryToCart(article: Article, quantityAndUnitValue: QuantityAndUnitValue, cartType: CartType): void {
    if (!!article) {
      const unit = article.units?.find((u) => u.code === quantityAndUnitValue?.unitCode);

      this.activeCartService.addEntry(
        article.code,
        quantityAndUnitValue?.quantity,
        unit,
        cartType,
        undefined,
        undefined,
        undefined,
        ItemListType.ASSORTMENT
      );
    }
  }

  private needsToLoad(loaderState: StateUtils.LoaderState<any>) {
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }
}
